/**
 * @file ColorSample.tsx
 * @description Образец и контраст в окне цвета (phase19.16 Р1;
 * uiux-design-r1 §1 «Окно цвета»): кнопка «Смотреть каталог →», ссылка
 * «Подробнее» и бейдж «Новинка» на фоне страницы в цветах черновика с новым
 * цветом; строки «Текст на кнопке — белый, контраст 7,3 ✓» и «Кнопка на фоне
 * страницы — 6,8 ✓»; если ни белый, ни тёмный не читаются — подсказка с
 * «Взять ближайший читаемый».
 */

import { deriveSiteColors, type SiteColorRole, type SiteColors } from "@chaiberi/shared";

import { Button } from "../../ui/button";
import { buttonTextView, colorsValid, roleContrast } from "../../../lib/site-view/color-roles";

interface Props {
  role: SiteColorRole;
  colors: SiteColors;
  onApply: (hex: string) => void;
}

export function ColorSample({ role, colors, onApply }: Props) {
  if (!colorsValid(colors)) {
    return <p className="type-caption text-on-surface-variant">Введите цвет в формате #RRGGBB.</p>;
  }
  const derived = deriveSiteColors(colors);
  const button = buttonTextView(colors);
  const contrast = roleContrast(colors, role);

  return (
    <div className="flex flex-col gap-2">
      <div
        className="flex flex-wrap items-center gap-3 rounded-xl p-4 [box-shadow:inset_0_0_0_1px_var(--border)]"
        style={{ background: colors.pageBg, color: colors.text }}
        aria-label="Образец на фоне страницы"
      >
        <span className="rounded-md px-4 py-2 text-sm font-medium" style={{ background: colors.button, color: derived.buttonText }}>
          Смотреть каталог →
        </span>
        <span className="text-sm font-medium underline" style={{ color: colors.accent }}>
          Подробнее
        </span>
        <span className="rounded px-2 py-0.5 text-xs font-medium" style={{ background: colors.button, color: derived.buttonText }}>
          НОВИНКА
        </span>
        <span className="text-sm" style={{ color: colors.textMuted }}>
          подпись
        </span>
      </div>
      {role === "button" && button ? (
        <>
          <p className="type-body text-on-surface">
            Текст на кнопке — {button.toneLabel}, контраст {button.ratioLabel} {button.mark}
          </p>
          {button.level !== "ok" ? (
            <div className="flex flex-col gap-1">
              <p className="type-caption text-on-surface-variant">
                Ни белый, ни тёмный текст на этом цвете не читается (лучший — {button.ratioLabel} при норме 4,5).
              </p>
              {button.nearestButton ? (
                <Button variant="outline" className="h-auto max-w-full self-start whitespace-normal py-2 text-left" onClick={() => onApply(button.nearestButton!)}>
                  Взять ближайший читаемый {button.nearestButton.toUpperCase()}
                </Button>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
      {contrast ? (
        <p className="type-body text-on-surface">
          {role === "button" ? "Кнопка на фоне страницы" : "Контраст с фоном"} — {contrast.ratioLabel} {contrast.mark}
        </p>
      ) : null}
      {contrast?.nearest ? (
        <Button variant="outline" className="h-auto max-w-full self-start whitespace-normal py-2 text-left" onClick={() => onApply(contrast.nearest!.hex)}>
          Взять ближайший {contrast.nearest.hex.toUpperCase()} · {contrast.nearest.ratioLabel}
        </Button>
      ) : null}
    </div>
  );
}
